export const THEME_PREFERENCE_STORAGE_KEY =
  "fishystuff.theme-preference.v1";
export const THEME_BOOT_CACHE_STORAGE_KEY = "fishystuff.theme-boot.v1";

const LEGACY_UI_SETTINGS_STORAGE_KEY = "fishystuff.ui.settings.v1";
const LEGACY_THEME_STORAGE_KEY = "theme";
const LEGACY_THEME_PATH = Object.freeze(["app", "theme", "selected"]);

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function result(kind, fields = {}) {
  return Object.freeze({ kind, ...fields });
}

function removePath(root, path) {
  if (!isRecord(root) || path.length === 0 || !Object.hasOwn(root, path[0])) {
    return root;
  }
  const next = { ...root };
  if (path.length === 1) {
    delete next[path[0]];
    return next;
  }
  const child = removePath(next[path[0]], path.slice(1));
  if (isRecord(child) && Object.keys(child).length > 0) {
    next[path[0]] = child;
  } else {
    delete next[path[0]];
  }
  return next;
}

function legacyUiSelection(raw) {
  if (raw === null) return result("missing");
  let document;
  try {
    document = JSON.parse(raw);
  } catch (_error) {
    // This shared blob may contain unrelated corrupt settings. Without a
    // decodable exact path, the theme importer does not claim those bytes.
    return result("missing");
  }
  if (!isRecord(document)) return result("missing");
  const app = isRecord(document.app) ? document.app : null;
  const theme = app && isRecord(app.theme) ? app.theme : null;
  if (!theme || !Object.hasOwn(theme, "selected")) return result("missing");
  return result("candidate", {
    raw,
    source: "ui-settings",
    value: theme.selected,
  });
}

export function createLocalThemePreferenceProvider({
  storage = null,
  eventTarget = null,
  legacySettings = null,
} = {}) {
  let disposed = false;
  const invalidationListeners = new Set();

  function readKey(key, operation = "read") {
    try {
      if (!storage || typeof storage.getItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      return result("available", { raw: storage.getItem(key) });
    } catch (_error) {
      return result("unavailable", {
        operation,
        reason: "storage-read-failed",
      });
    }
  }

  function writeKey(key, raw) {
    try {
      if (
        !storage
        || typeof storage.setItem !== "function"
        || typeof storage.getItem !== "function"
      ) {
        throw new TypeError("Storage is unavailable.");
      }
      storage.setItem(key, raw);
      if (storage.getItem(key) !== raw) {
        return result("unavailable", {
          operation: "write",
          reason: "storage-write-not-acknowledged",
        });
      }
      return result("written");
    } catch (_error) {
      return result("unavailable", {
        operation: "write",
        reason: "storage-write-failed",
      });
    }
  }

  function removeKey(key) {
    try {
      if (
        !storage
        || typeof storage.removeItem !== "function"
        || typeof storage.getItem !== "function"
      ) {
        throw new TypeError("Storage is unavailable.");
      }
      if (storage.getItem(key) === null) return result("removed");
      storage.removeItem(key);
      if (storage.getItem(key) !== null) {
        return result("unavailable", {
          operation: "remove",
          reason: "storage-remove-not-acknowledged",
        });
      }
      return result("removed");
    } catch (_error) {
      return result("unavailable", {
        operation: "remove",
        reason: "storage-remove-failed",
      });
    }
  }

  function readDocument() {
    return readKey(THEME_PREFERENCE_STORAGE_KEY);
  }

  function writeDocument(raw) {
    return writeKey(THEME_PREFERENCE_STORAGE_KEY, raw);
  }

  function removeDocument() {
    return removeKey(THEME_PREFERENCE_STORAGE_KEY);
  }

  function readLegacySelection() {
    const settings = readKey(LEGACY_UI_SETTINGS_STORAGE_KEY, "legacy-read");
    if (settings.kind === "unavailable") return settings;
    const selected = legacyUiSelection(settings.raw);
    if (selected.kind === "candidate") return selected;

    const standalone = readKey(LEGACY_THEME_STORAGE_KEY, "legacy-read");
    if (standalone.kind === "unavailable") return standalone;
    if (standalone.raw === null) return result("missing");
    return result("candidate", {
      raw: standalone.raw,
      source: "theme",
      value: standalone.raw,
    });
  }

  function removeLegacyUiSelection() {
    const before = readKey(LEGACY_UI_SETTINGS_STORAGE_KEY, "legacy-remove");
    if (before.kind === "unavailable") return before;
    if (before.raw === null) return result("removed");

    let document;
    try {
      document = JSON.parse(before.raw);
    } catch (_error) {
      return result("removed");
    }
    if (!isRecord(document)) return result("removed");
    const selected = legacyUiSelection(before.raw);
    if (selected.kind !== "candidate") return result("removed");

    try {
      if (typeof legacySettings?.remove === "function") {
        legacySettings.remove(
          LEGACY_THEME_PATH,
          "theme-preference-import",
        );
      } else {
        const next = removePath(document, LEGACY_THEME_PATH);
        if (Object.keys(next).length === 0) {
          const removed = removeKey(LEGACY_UI_SETTINGS_STORAGE_KEY);
          if (removed.kind === "unavailable") return removed;
        } else {
          const written = writeKey(
            LEGACY_UI_SETTINGS_STORAGE_KEY,
            JSON.stringify(next),
          );
          if (written.kind === "unavailable") return written;
        }
      }
    } catch (_error) {
      return result("unavailable", {
        operation: "legacy-remove",
        reason: "legacy-ui-settings-remove-failed",
      });
    }

    const after = readKey(LEGACY_UI_SETTINGS_STORAGE_KEY, "legacy-remove");
    if (after.kind === "unavailable") return after;
    if (legacyUiSelection(after.raw).kind === "candidate") {
      return result("unavailable", {
        operation: "legacy-remove",
        reason: "legacy-ui-settings-remove-not-acknowledged",
      });
    }
    return result("removed");
  }

  function removeLegacySelections() {
    const settings = removeLegacyUiSelection();
    if (settings.kind === "unavailable") return settings;
    const standalone = removeKey(LEGACY_THEME_STORAGE_KEY);
    if (standalone.kind === "unavailable") {
      return result("unavailable", {
        operation: "legacy-remove",
        reason: standalone.reason,
      });
    }
    return result("removed");
  }

  function subscribeInvalidation(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Provider invalidation listener must be a function.");
    }
    if (disposed) return () => {};
    invalidationListeners.add(listener);
    return () => invalidationListeners.delete(listener);
  }

  function invalidate() {
    if (disposed) return;
    for (const listener of invalidationListeners) {
      try {
        listener();
      } catch (error) {
        globalThis.reportError?.(error);
      }
    }
  }

  function handleStorage(event) {
    if (
      event
      && (
        event.key === THEME_PREFERENCE_STORAGE_KEY
        || event.key === null
      )
    ) {
      invalidate();
    }
  }

  function handlePageShow(event) {
    if (event?.persisted === true) invalidate();
  }

  eventTarget?.addEventListener?.("storage", handleStorage);
  eventTarget?.addEventListener?.("pageshow", handlePageShow);

  function dispose() {
    if (disposed) return;
    disposed = true;
    invalidationListeners.clear();
    eventTarget?.removeEventListener?.("storage", handleStorage);
    eventTarget?.removeEventListener?.("pageshow", handlePageShow);
  }

  return Object.freeze({
    dispose,
    readDocument,
    readLegacySelection,
    removeDocument,
    removeLegacySelections,
    subscribeInvalidation,
    writeDocument,
  });
}

export function createLocalThemeBootCacheProjectionProvider({
  storage = null,
} = {}) {
  // The active appearance composition owns when this disposable projection is
  // updated. The signed-out canonical repository never calls this provider.
  function unavailable(operation, reason) {
    return result("unavailable", { operation, reason });
  }

  function writeReady(raw) {
    if (typeof raw !== "string") {
      throw new TypeError("Theme boot-cache projection must be encoded text.");
    }
    try {
      if (
        !storage
        || typeof storage.setItem !== "function"
        || typeof storage.getItem !== "function"
      ) {
        throw new TypeError("Storage is unavailable.");
      }
      storage.setItem(THEME_BOOT_CACHE_STORAGE_KEY, raw);
      if (storage.getItem(THEME_BOOT_CACHE_STORAGE_KEY) !== raw) {
        return unavailable("write", "storage-write-not-acknowledged");
      }
      return result("acknowledged");
    } catch (_error) {
      return unavailable("write", "storage-write-failed");
    }
  }

  function remove() {
    try {
      if (
        !storage
        || typeof storage.removeItem !== "function"
        || typeof storage.getItem !== "function"
      ) {
        throw new TypeError("Storage is unavailable.");
      }
      storage.removeItem(THEME_BOOT_CACHE_STORAGE_KEY);
      if (storage.getItem(THEME_BOOT_CACHE_STORAGE_KEY) !== null) {
        return unavailable("remove", "storage-remove-not-acknowledged");
      }
      return result("acknowledged");
    } catch (_error) {
      return unavailable("remove", "storage-remove-failed");
    }
  }

  return Object.freeze({ remove, writeReady });
}

export function createBrowserThemeBootCacheProjectionProvider() {
  let storage = null;
  try {
    storage = globalThis.localStorage ?? null;
  } catch (_error) {
    storage = null;
  }
  return createLocalThemeBootCacheProjectionProvider({ storage });
}

export function createBrowserLocalThemePreferenceProvider() {
  let storage = null;
  try {
    storage = globalThis.localStorage ?? null;
  } catch (_error) {
    storage = null;
  }
  return createLocalThemePreferenceProvider({
    storage,
    eventTarget: globalThis.window ?? null,
    legacySettings: globalThis.window?.__fishystuffUiSettings ?? null,
  });
}
